import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

type Art = 'gitlab' | 'github' | 'coolify' | 'dns'

/**
 * **Was je Zugang einzugeben ist – und woher man es bekommt.** Steht an der Maske, nicht in
 * einer Anleitung: wer hier ein Token einträgt, hat gerade das andere Fenster offen.
 */
const FELDER: Record<
  Art,
  {
    url: boolean
    konto: string | null
    kontoBeispiel?: string
    tokenLabel: string
    tokenBeispiel: string
    woher: string
    links: { href: string; text: string }[]
  }
> = {
  gitlab: {
    url: false,
    konto: 'Gruppe',
    kontoBeispiel: 'epcan-main/netcon',
    tokenLabel: 'Token',
    tokenBeispiel: 'glpat-…',
    woher:
      'Gruppen-Token mit Rolle Maintainer und Scope api – für Deploy-Keys und Webhooks in den Repos. Gibt es den Menüpunkt nicht (gitlab.com ohne Premium), ein persönliches Token mit Scope api von einem Konto, das in der Gruppe Maintainer ist.',
    links: [
      {
        href: 'https://gitlab.com/groups/epcan-main/netcon/-/settings/access_tokens',
        text: 'Gruppen-Token (epcan-main/netcon)',
      },
      {
        href: 'https://gitlab.com/-/user_settings/personal_access_tokens',
        text: 'persönliches Token',
      },
    ],
  },
  github: {
    url: false,
    konto: 'Organisation',
    kontoBeispiel: 'epcan',
    tokenLabel: 'Token',
    tokenBeispiel: 'github_pat_…',
    woher:
      'Fine-grained Token: Resource owner = die Organisation, „All repositories“; Metadata + Contents lesen, Administration + Webhooks lesen & schreiben. Ggf. muss ein Org-Owner das Token freigeben.',
    links: [
      { href: 'https://github.com/settings/personal-access-tokens/new', text: 'Token anlegen' },
      { href: 'https://github.com/settings/organizations', text: 'deine Organisationen' },
    ],
  },
  coolify: {
    url: true,
    konto: null,
    tokenLabel: 'Token',
    tokenBeispiel: 'Token aus Keys & Tokens › API Tokens',
    woher:
      'Erst unter Einstellungen › Erweitert „API Access“ einschalten und die Work-IP 92.205.130.43 freigeben. Dann ein API-Token mit write, deploy und read:sensitive.',
    links: [
      { href: 'https://repo.netcon.cloud/settings/advanced', text: 'Einstellungen › Erweitert' },
      { href: 'https://repo.netcon.cloud/security/api-tokens', text: 'API Tokens' },
    ],
  },
  dns: {
    url: false,
    konto: 'Benutzername',
    kontoBeispiel: 'epcan.de',
    tokenLabel: 'Passwort',
    tokenBeispiel: 'Passwort des Reselling-Logins',
    woher:
      'Login des Reselling-Kontos (am besten ein eigener Benutzer nur für die API). Ist dort eine IP-Freigabe für die API eingestellt, die Work-IP 92.205.130.43 eintragen.',
    links: [{ href: 'https://domainreselling.de/v3/', text: 'Reselling-Oberfläche' }],
  },
}

const standQuery = {
  queryKey: ['umgebung', 'stand'],
  queryFn: () => trpc.umgebung.stand.query(),
  staleTime: 30_000,
  retry: false,
} as const

/**
 * **Einstellungen › Umgebungen** (Schritt 1, 23.10.2026, `mockup-umgebung.html` ③).
 *
 * Zugänge zu GitLab, GitHub, Coolify und united-domains Reselling, dazu die Zonen, unter denen
 * Work später A-Records anlegen darf. Tokens sind nur schreibend – wie bei der KI.
 */
export function UmgebungEinstellungen() {
  return (
    <>
      <Zugaenge />
      <Zonen />
    </>
  )
}

function Pruefstand({
  am,
  ok,
  info,
  fehler,
}: {
  am: Date | string | null
  ok: boolean | null
  info: string | null
  fehler: string | null
}) {
  if (am === null) return <span className="text-muted">noch nie geprüft</span>
  const wann = new Date(am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
  return ok ? (
    <span className="text-good-text">
      ✓ {info} <span className="text-muted">· {wann}</span>
    </span>
  ) : (
    <span className="text-bad">
      ✗ {fehler} <span className="text-muted">· {wann}</span>
    </span>
  )
}

function Zugaenge() {
  const { data: stand, error } = useQuery(standQuery)
  const [offen, setOffen] = useState<Art | null>(null)
  const queryClient = useQueryClient()
  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['umgebung'] })

  const pruefen = useMutation({
    mutationFn: (art: Art) => trpc.umgebung.pruefen.mutate({ art }),
    onSettled: neuLaden,
  })
  const entfernen = useMutation({
    mutationFn: (art: Art) => trpc.umgebung.zugangEntfernen.mutate({ art }),
    onSettled: neuLaden,
  })

  if (error) return null

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">Zugänge</h3>
          <span className="text-[11.5px] text-muted">
            Tokens werden verschlüsselt gespeichert und nie zurückgegeben
          </span>
        </div>

        {stand && !stand.schluesselVorhanden && (
          <p className="mt-2 text-[12px] text-bad">
            <strong>CREDENTIAL_KEY fehlt in der `.env`.</strong> Tokens lassen sich damit nicht
            lesen.
          </p>
        )}

        <div className="mt-2">
          {stand?.zugaenge.map((z) => {
            const art = z.art as Art
            const h = z.hinterlegt
            return (
              <div key={art} className="border-t border-line py-2.5 first:border-t-0">
                <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                  <div className="w-[170px] shrink-0 text-[12.5px] font-semibold">{z.label}</div>
                  <div className="min-w-0 flex-1 text-[12px] break-words">
                    {h ? (
                      <>
                        <div className="text-ink2">
                          {[
                            h.url ?? z.standardUrl,
                            h.konto,
                            art === 'dns' ? 'Passwort hinterlegt' : `Token …${h.endetAuf}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                        <div className="mt-0.5">
                          <Pruefstand
                            am={h.letztePruefungAm}
                            ok={h.letztePruefungOk}
                            info={h.letztePruefungInfo}
                            fehler={h.letzterFehler}
                          />
                        </div>
                      </>
                    ) : (
                      <span className="text-muted">nicht hinterlegt</span>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    {h && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={pruefen.isPending && pruefen.variables === art}
                        onClick={() => pruefen.mutate(art)}
                      >
                        {pruefen.isPending && pruefen.variables === art ? 'prüft …' : 'prüfen'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={h ? 'ghost' : 'primary'}
                      onClick={() => setOffen(offen === art ? null : art)}
                    >
                      {offen === art ? 'schließen' : h ? 'ändern' : 'einrichten'}
                    </Button>
                    {h && (
                      <Button size="sm" variant="ghost" onClick={() => entfernen.mutate(art)}>
                        entfernen
                      </Button>
                    )}
                  </div>
                </div>
                {offen === art && (
                  <ZugangFormular
                    art={art}
                    vorhanden={h ? { url: h.url, konto: h.konto } : null}
                    standardUrl={z.standardUrl}
                    fertig={() => {
                      setOffen(null)
                      neuLaden()
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </CardPad>
    </Card>
  )
}

function ZugangFormular({
  art,
  vorhanden,
  standardUrl,
  fertig,
}: {
  art: Art
  vorhanden: { url: string | null; konto: string | null } | null
  standardUrl: string | null
  fertig: () => void
}) {
  const f = FELDER[art]
  const [url, setUrl] = useState(vorhanden?.url ?? '')
  const [konto, setKonto] = useState(vorhanden?.konto ?? '')
  const [token, setToken] = useState('')
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null)

  const speichern = useMutation({
    mutationFn: () =>
      trpc.umgebung.zugangSetzen.mutate({
        art,
        ...(token.trim() ? { token: token.trim() } : {}),
        ...(url.trim() ? { url: url.trim() } : {}),
        ...(konto.trim() ? { konto: konto.trim() } : {}),
      }),
    onSuccess: (r) => {
      setToken('')
      if (r.ok) fertig()
      else setMeldung({ ok: false, text: `Gespeichert, aber die Prüfung schlug fehl: ${r.fehler}` })
    },
    onError: (e: Error) => setMeldung({ ok: false, text: e.message }),
  })

  return (
    <form
      className="mt-2.5 rounded-lg bg-[#f7f7f5] p-3"
      onSubmit={(e) => {
        e.preventDefault()
        setMeldung(null)
        speichern.mutate()
      }}
    >
      <p className="text-[11.5px] text-ink2">{f.woher}</p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[11.5px]">
        {f.links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            target="_blank"
            rel="noreferrer"
            className="text-accent underline-offset-2 hover:underline"
          >
            {l.text} ↗
          </a>
        ))}
      </p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        {f.url && (
          <label className="text-[12px]">
            <span className="block text-muted">Adresse</span>
            <input
              className="mt-0.5 w-[260px] max-w-full rounded-md border border-line bg-surface px-2 py-1"
              placeholder={standardUrl ?? 'https://…'}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
        )}
        {f.konto && (
          <label className="text-[12px]">
            <span className="block text-muted">{f.konto}</span>
            <input
              className="mt-0.5 w-[180px] max-w-full rounded-md border border-line bg-surface px-2 py-1"
              placeholder={f.kontoBeispiel}
              value={konto}
              onChange={(e) => setKonto(e.target.value)}
            />
          </label>
        )}
        <label className="text-[12px]">
          <span className="block text-muted">
            {f.tokenLabel}
            {vorhanden ? ' (leer lassen = bleibt)' : ''}
          </span>
          <input
            type="password"
            autoComplete="off"
            className="mt-0.5 w-[280px] max-w-full rounded-md border border-line bg-surface px-2 py-1 font-mono"
            placeholder={f.tokenBeispiel}
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <Button type="submit" size="sm" disabled={speichern.isPending}>
          {speichern.isPending ? 'wird geprüft …' : 'Speichern und prüfen'}
        </Button>
      </div>
      {meldung && <p className="mt-2 text-[12px] text-bad">✗ {meldung.text}</p>}
    </form>
  )
}

/* ── Freigegebene Domainzonen ─────────────────────────────────────────── */

interface ZonenEntwurf {
  suffix: string
  gesperrt: string
  ziel: string
  eigeneIp: string
}

const LEER: ZonenEntwurf = { suffix: '', gesperrt: '', ziel: '', eigeneIp: '' }

function Zonen() {
  const { data: stand, error } = useQuery(standQuery)
  const dnsDa = stand?.zugaenge.some((z) => z.art === 'dns' && z.hinterlegt) ?? false
  const coolifyDa = stand?.zugaenge.some((z) => z.art === 'coolify' && z.hinterlegt) ?? false
  const { data: kontoZonen } = useQuery({
    queryKey: ['umgebung', 'dnsZonen'],
    queryFn: () => trpc.umgebung.dnsZonen.query(),
    enabled: dnsDa,
    staleTime: 60_000,
    retry: false,
  })
  const { data: server } = useQuery({
    queryKey: ['umgebung', 'coolifyServer'],
    queryFn: () => trpc.umgebung.coolifyServer.query(),
    enabled: coolifyDa,
    staleTime: 60_000,
    retry: false,
  })
  const [entwurf, setEntwurf] = useState<ZonenEntwurf | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['umgebung', 'stand'] })

  const serverListe = server?.server ?? []

  const speichern = useMutation({
    mutationFn: (e: ZonenEntwurf) => {
      const s = serverListe.find((x) => x.uuid === e.ziel)
      const zielIp = e.ziel === 'eigen' ? e.eigeneIp.trim() : (s?.ip ?? null)
      return trpc.umgebung.zoneSetzen.mutate({
        suffix: e.suffix,
        gesperrt: e.gesperrt.split(/[\s,;]+/).filter(Boolean),
        zielIp: zielIp || null,
        zielLabel: e.ziel === 'eigen' ? 'eigene IP' : (s?.name ?? null),
      })
    },
    onSuccess: () => {
      setEntwurf(null)
      setFehler(null)
      neuLaden()
    },
    onError: (e: Error) => setFehler(e.message),
  })
  const entfernen = useMutation({
    mutationFn: (id: string) => trpc.umgebung.zoneEntfernen.mutate({ id }),
    onSettled: neuLaden,
  })

  if (error || !stand) return null
  const zonen = stand.zonen

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">Freigegebene Domainzonen</h3>
          <span className="text-[11.5px] text-muted">
            Nur darunter legt Work A-Records an – eine Ebene, nie über Vorhandenes
          </span>
          {!entwurf && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              disabled={!dnsDa}
              title={dnsDa ? undefined : 'Erst den DNS-Zugang hinterlegen'}
              onClick={() => setEntwurf(LEER)}
            >
              + Zone freigeben
            </Button>
          )}
        </div>

        {!dnsDa && (
          <p className="mt-2 text-[12px] text-muted">
            Zonen lassen sich freigeben, sobald der Zugang zu united-domains Reselling hinterlegt
            ist.
          </p>
        )}
        {kontoZonen?.fehler && dnsDa && (
          <p className="mt-2 text-[12px] text-bad">✗ united-domains: {kontoZonen.fehler}</p>
        )}

        {zonen.length > 0 && (
          <div className="overflow-x-auto">
            <table className="mt-3 w-full min-w-[620px] border-collapse text-[12px]">
              <thead>
                <tr>
                  {['Freigegeben', 'liegt in Zone', 'Gesperrte Namen', 'Ziel-Vorschlag', ''].map(
                    (h) => (
                      <th
                        key={h}
                        className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {zonen.map((z) => (
                  <tr key={z.id}>
                    <td className="border-b border-line px-2 py-1.5 font-mono">*.{z.suffix}</td>
                    <td className="border-b border-line px-2 py-1.5 font-mono text-ink2">
                      {z.dnsZoneName}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      {z.gesperrt.length ? (
                        <span className="font-mono text-[11.5px] text-bad">
                          {z.gesperrt.join(' ')}
                        </span>
                      ) : (
                        <span className="text-muted">–</span>
                      )}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      {z.zielIp ? (
                        <>
                          {z.zielLabel ?? ''} <span className="font-mono">{z.zielIp}</span>
                        </>
                      ) : (
                        <span className="text-muted">beim Anlegen wählen</span>
                      )}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!dnsDa}
                          onClick={() => {
                            const s = serverListe.find((x) => x.ip === z.zielIp)
                            setEntwurf({
                              suffix: z.suffix,
                              gesperrt: z.gesperrt.join(' '),
                              ziel: z.zielIp ? (s ? s.uuid : 'eigen') : '',
                              eigeneIp: s ? '' : (z.zielIp ?? ''),
                            })
                          }}
                        >
                          bearbeiten
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => entfernen.mutate(z.id)}>
                          entfernen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {entwurf && (
          <form
            className="mt-3 rounded-lg bg-[#f7f7f5] p-3"
            onSubmit={(e) => {
              e.preventDefault()
              speichern.mutate(entwurf)
            }}
          >
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-[12px]">
                <span className="block text-muted">Domain (alles darunter)</span>
                <span className="mt-0.5 flex items-center rounded-md border border-line bg-surface">
                  <span className="pl-2 font-mono text-muted">*.</span>
                  <input
                    className="w-[200px] max-w-full rounded-md bg-transparent px-1 py-1 font-mono"
                    list="umgebung-kontozonen"
                    placeholder="app.netcon.cloud"
                    value={entwurf.suffix}
                    onChange={(e) => setEntwurf({ ...entwurf, suffix: e.target.value })}
                  />
                </span>
                <datalist id="umgebung-kontozonen">
                  {kontoZonen?.zonen.map((z) => (
                    <option key={z.id} value={z.name} />
                  ))}
                </datalist>
              </label>
              <label className="text-[12px]">
                <span className="block text-muted">Gesperrte Namen</span>
                <input
                  className="mt-0.5 w-[220px] max-w-full rounded-md border border-line bg-surface px-2 py-1 font-mono"
                  placeholder="work www mail @"
                  value={entwurf.gesperrt}
                  onChange={(e) => setEntwurf({ ...entwurf, gesperrt: e.target.value })}
                />
              </label>
              <label className="text-[12px]">
                <span className="block text-muted">Ziel-Vorschlag</span>
                <select
                  className="mt-0.5 rounded-md border border-line bg-surface px-2 py-1"
                  value={entwurf.ziel}
                  onChange={(e) => setEntwurf({ ...entwurf, ziel: e.target.value })}
                >
                  <option value="">keiner – beim Anlegen wählen</option>
                  {serverListe.map((s) => (
                    <option key={s.uuid} value={s.uuid}>
                      {s.name} · {s.ip}
                    </option>
                  ))}
                  <option value="eigen">eigene IP …</option>
                </select>
              </label>
              {entwurf.ziel === 'eigen' && (
                <label className="text-[12px]">
                  <span className="block text-muted">IPv4</span>
                  <input
                    className="mt-0.5 w-[140px] rounded-md border border-line bg-surface px-2 py-1 font-mono"
                    placeholder="203.0.113.10"
                    value={entwurf.eigeneIp}
                    onChange={(e) => setEntwurf({ ...entwurf, eigeneIp: e.target.value })}
                  />
                </label>
              )}
              <Button type="submit" size="sm" disabled={speichern.isPending || !entwurf.suffix}>
                {speichern.isPending ? 'prüft …' : 'Freigeben'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEntwurf(null)
                  setFehler(null)
                }}
              >
                abbrechen
              </Button>
            </div>
            {server?.fehler && coolifyDa && (
              <p className="mt-2 text-[11.5px] text-muted">
                Coolify-Server nicht abrufbar ({server.fehler}) – eigene IP geht trotzdem.
              </p>
            )}
            {fehler && <p className="mt-2 text-[12px] text-bad">✗ {fehler}</p>}
          </form>
        )}
      </CardPad>
    </Card>
  )
}
